import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { LOADING_STATUS } from "../../common/constants";
//import { TranslationItem } from '../../common/types/translations.types';
import {
  InitialPaginationParameters,
  InitialPaginationResponse,
  PaginationParameters,
  PaginationResponse,
  PopulatePagination,
} from "../../types/pagination.types";
import { apiService } from "../../common/api/apiService";
import { API_ENDPOINTS } from "../../common/api/apiEndpoints";

export enum CustomerFeedBackListOrderByDirectionOptions {
  Ascending,
  Descending,
}
export enum CustomerFeedBackListOrderByOptions {
  Random = 0,
  BookingNumber = 1,
  AccommodationNumber = 2,
  AnswerTime = 3,
  SentTime = 4,
  Rating = 5,
  CustomerLastName = 6,
  FeedbackState = 7,
  OwnerLastName = 8,
  BookingStartTime = 9,
}

export interface FeedbacksFilter extends PaginationParameters {
  feedbackState:
    | "New"
    | "InProgress"
    | "Approved"
    | "Rejected"
    | "WaitingForAnswer"
    | "PartiallyApproved";
  accommodationId: string;
  ownerId: string;
  orderBy: CustomerFeedBackListOrderByOptions;
  FeedbackFetchAnswerStartDate: Date;
  FeedbackFetchAnswerEndDate: Date;
  OrderDirection: CustomerFeedBackListOrderByDirectionOptions;
}
export interface FeedbackQuestion {
  id: string;
  answerPossible: boolean;
  approvable: boolean;
  modifiable: boolean;
  questionId: number;
  text: string;
  type: "RATING" | "TEXT" | "SELECTION" | "SCORE";
  serviceQuestion: boolean;
  ownerQuestion: boolean;
}

export interface FeedbackAnswer {
  id: string;
  indexNumber?: number;
  numbericValue?: number;
  originalNumbericValue?: number;
  textValue?: string;
  originalTextValue?: string;
  approved?: boolean;
  stateModified?: Date;
  reply?: string;
  question: FeedbackQuestion;
}
export interface FeedbackOwnerCommentDto {
  id?: string;
  ownerComment: string;
}
export interface FeedbackDetails {
  id?: string;
  isDeleted?: boolean;
  sentTime: string;
  answerTime?: Date;
  dateOfPublication?: string;
  feedbackState: string;
  contactRequested: boolean;
  customerFirstName: string;
  customerLastName: string;
  customerNumber: string;
  customerEmail: string;
  ownerEmail: string;
  cleanerEmail: string;
  caretakerEmail: string;
  ownerBookingNoteEmail: string;
  accommodationNumber: string;
  accommodationName: string;
  accommodationImageUrl: string;
  bookingStartTime: string;
  bookingEndTime: string;
  bookingNumber: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerNumber: string;
  siteId: string;
  locale: string;
  approvedRejectedTime: string;
  ownerReplySentTime: string;
  caretakerReplySentTime: string;
  answers: Array<FeedbackAnswer>;
  serviceComment: string;
  ownerComment: string;
}

export interface FeedbackListItem {
  id: string;
  sentTime: string;
  answerTime: string;
  feedbackState: string;
  contactRequested: boolean;
  customerFirstName: string;
  customerLastName: string;
  customerNumber: string;
  accommodationNumber: string;
  accommodationName: string;
  bookingStartTime: Date;
  bookingEndTime: Date;
  bookingNumber: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerNumber: string;
  siteId: string;
  locale: string;
  approvedRejectedTime: Date;
  ownerReplySentTime: Date;
  caretakerReplySentTime: Date;
  rating: number;
  feedBackText: string;
}

export interface FeedbacksState {
  feedbackItems: FeedbackListItem[];
  feedbackDetails: FeedbackDetails;
  feedbackItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  feedbackDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  feedbackPagination: PaginationResponse;
}

const initialState: FeedbacksState = {
  feedbackItems: [],
  feedbackDetails: {
    id: "",
    isDeleted: false,
    sentTime: new Date().toISOString(),
    feedbackState: "",
    contactRequested: false,
    customerFirstName: "",
    customerLastName: "",
    customerNumber: "",
    customerEmail: "",
    ownerEmail: "",
    cleanerEmail: "",
    caretakerEmail: "",
    ownerBookingNoteEmail: "",
    accommodationNumber: "",
    accommodationName: "",
    accommodationImageUrl: "",
    bookingStartTime: new Date().toISOString(),
    bookingEndTime: new Date().toISOString(),
    bookingNumber: "",
    ownerFirstName: "",
    ownerLastName: "",
    ownerNumber: "",
    siteId: "",
    locale: "",
    approvedRejectedTime: new Date().toISOString(),
    ownerReplySentTime: new Date().toISOString(),
    caretakerReplySentTime: new Date().toISOString(),
    answers: [],
    serviceComment: "",
    ownerComment: "",
  },
  feedbackItemsStatus: LOADING_STATUS.IDLE,
  feedbackDetailsStatus: LOADING_STATUS.IDLE,
  feedbackPagination: InitialPaginationResponse,
};
export const getFeedbacksAsync = createAsyncThunk(
  "customerfeedbacks/getCustomerFeedbacks",
  async (feedbacksFilter: FeedbacksFilter, { rejectWithValue }) => {
    const feedBacksInfo = await apiService.post(
      API_ENDPOINTS.FEEDBACKS.GET_FEEDBACKS,
      feedbacksFilter,
    );
    return { feedBacksInfo };
  },
);

export const getFeedbackDetailsAsync = createAsyncThunk(
  "customerfeedbacks/getFeedbackDetails",
  async (feedbackId: string, { rejectWithValue }) => {
    const feedBackInfo = await apiService.get(
      API_ENDPOINTS.FEEDBACKS.GET_FEEDBACK + "?feedbackId=" + feedbackId,
    );
    return { feedBackInfo };
  },
);

export const updateFeedbackAsync = createAsyncThunk(
  "customerfeedbacks/updateFeedback",
  async (
    feedbackOwnerComment: FeedbackOwnerCommentDto,
    { rejectWithValue },
  ) => {
    const feedBacksInfo = await apiService.post(
      API_ENDPOINTS.FEEDBACKS.SET_FEEDBACK_OWNER_COMMENT,
      feedbackOwnerComment,
    );
    return { feedBacksInfo };
  },
);

const feedbacksSlice = createSlice({
  name: "feedbacks",
  initialState,
  reducers: {
    changeStateFeedbackOwnerComment: (state, action) => {
      const { ownerCommentChanged } = action.payload;
      state.feedbackDetails.ownerComment = ownerCommentChanged;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFeedbacksAsync.pending, (state) => {
        state.feedbackItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getFeedbacksAsync.fulfilled, (state, action) => {
        state.feedbackItemsStatus = LOADING_STATUS.IDLE;
        state.feedbackItems = action.payload.feedBacksInfo.data;
        state.feedbackPagination = PopulatePagination(
          action.payload.feedBacksInfo,
        );
      })
      .addCase(getFeedbacksAsync.rejected, (state) => {
        state.feedbackItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getFeedbackDetailsAsync.pending, (state) => {
        state.feedbackDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getFeedbackDetailsAsync.fulfilled, (state, action) => {
        state.feedbackDetailsStatus = LOADING_STATUS.IDLE;
        state.feedbackDetails = action.payload.feedBackInfo.result;
      })
      .addCase(getFeedbackDetailsAsync.rejected, (state) => {
        state.feedbackDetailsStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectFeedbackItems = (state: RootState) =>
  state.feedbacks.feedbackItems;
export const selectFeedbackPagination = (state: RootState) =>
  state.feedbacks.feedbackPagination;
export const selectFeedbackItemsLoadingStatus = (state: RootState) =>
  state.feedbacks.feedbackItemsStatus;
export const selectFeedbackDetails = (state: RootState) =>
  state.feedbacks.feedbackDetails;
export const selectFeedbackDetailsLoadingStatus = (state: RootState) =>
  state.feedbacks.feedbackDetailsStatus;
export const { changeStateFeedbackOwnerComment } = feedbacksSlice.actions;

export default feedbacksSlice.reducer;
